import { t as translate } from '@utils/translate';

import type { GridStrings } from './GridStrings';

const NS = 'design-system';

function pickString(locale: 'en' | 'es', key: string, enFb: string, esFb: string): string {
  const path = `grid_demo.${key}`;
  const raw = translate(path, NS, locale);
  if (typeof raw === 'string' && raw !== path && raw.trim()) return raw;
  return locale === 'es' ? esFb : enFb;
}

function pickHeadingDescription(
  locale: 'en' | 'es',
  enFb: [string, string],
  esFb: [string, string]
): [string, string] {
  const path = 'grid_demo.heading_description';
  const raw = translate(path, NS, locale);
  if (
    Array.isArray(raw) &&
    raw.length >= 2 &&
    typeof raw[0] === 'string' &&
    typeof raw[1] === 'string'
  ) {
    return [raw[0].trim(), raw[1].trim()];
  }
  return locale === 'es' ? esFb : enFb;
}

export function buildGridDemoStrings(locale: 'en' | 'es'): GridStrings {
  return {
    headingTitle: pickString(locale, 'heading_title', 'Grid', 'Cuadrícula'),
    headingDescription: pickHeadingDescription(
      locale,
      [
        'Three patterns cover most product layouts: a balanced four-column grid, a content-friendly three-column grid, and a main region paired with an aside.',
        'On viewports below 768px, the grid examples are hidden so cards stay readable; the main + aside pattern below still demonstrates responsive behavior.',
      ],
      [
        'Tres patrones cubren la mayoría de layouts de producto: una cuadrícula equilibrada de cuatro columnas, una de tres columnas más amigable para contenido y una región principal con aside.',
        'Por debajo de 768px, los ejemplos de cuadrícula se ocultan para mantener tarjetas legibles; el patrón principal + aside demuestra el comportamiento responsive.',
      ]
    ),
    balancedFourTitle: pickString(
      locale,
      'balanced_four_title',
      'Balanced layout (4 columns)',
      'Layout equilibrado (4 columnas)'
    ),
    cardDefault: pickString(locale, 'card_default', 'Card default styling.', 'Tarjeta con estilo por defecto.'),
    cardSurface2: pickString(
      locale,
      'card_surface_2',
      'Card surface 2 color.',
      'Tarjeta con color de superficie 2.'
    ),
    cardSurface3: pickString(
      locale,
      'card_surface_3',
      'Card surface 3 color.',
      'Tarjeta con color de superficie 3.'
    ),
    cardSurface4: pickString(
      locale,
      'card_surface_4',
      'Card surface 4 color.',
      'Tarjeta con color de superficie 4.'
    ),
    contentFriendlyTitle: pickString(
      locale,
      'content_friendly_title',
      'Content-friendly layout (3 columns)',
      'Layout amigable para contenido (3 columnas)'
    ),
    asideSectionTitle: pickString(
      locale,
      'aside_section_title',
      'Grid with Sidebar',
      'Cuadrícula con barra lateral'
    ),
    collapsibleAsideSectionTitle: pickString(
      locale,
      'collapsible_aside_section_title',
      'Collapsible Grid with Sidebar',
      'Cuadrícula colapsable con barra lateral'
    ),
    collapsibleArticleHeaderTitle: pickString(
      locale,
      'collapsible_article_header_title',
      'Grid with Sidebar Article',
      'Artículo de cuadrícula con barra lateral'
    ),
    sidebarNavigationTitle: pickString(
      locale,
      'sidebar_navigation_title',
      'Sidebar Navigation',
      'Navegación de la barra lateral'
    ),
    sidebarDesktopBody: pickString(
      locale,
      'sidebar_desktop_body',
      'This Sidebar should be used for navigation or advertising purposes and is only visible to Desktop and Tablet browsers with screens wider than 992px.',
      'Esta barra lateral se debe usar para navegación o fines de publicidad y solo es visible en navegadores de escritorio y tablet con pantallas más anchas que 992px.'
    ),
    collapseAsideLabel: pickString(
      locale,
      'collapse_aside_label',
      'Hide Sidebar',
      'Ocultar barra lateral'
    ),
    expandAsideLabel: pickString(
      locale,
      'expand_aside_label',
      'Show Sidebar',
      'Mostrar barra lateral'
    ),
    asideNavLabel: pickString(locale, 'aside_nav_label', 'Side Panel', 'Panel lateral'),
    toggleShowAside: pickString(locale, 'toggle_show_aside', 'Show Sidebar', 'Mostrar barra lateral'),
    asideNavBody: pickString(
      locale,
      'aside_nav_body',
      'This Side Panel should be use for navigational purposes, and is visible to Mobile browsers with screens no wider than 992px.',
      'Este Panel lateral se debe usar con fines de navegación y es visible en navegadores móviles con pantallas de no más de 992px.'
    ),
    asideDesktopBody1: pickString(
      locale,
      'aside_desktop_body_1',
      'This Sidebar should be used for navigation or advertising purposes and is only visible to Desktop and Tablet browsers with screens wider than 992px.',
      'Esta barra lateral se debe usar para navegación o fines de publicidad y solo es visible en navegadores de escritorio y tablet con pantallas más anchas que 992px.'
    ),
    asideDesktopBody2: pickString(
      locale,
      'aside_desktop_body_2',
      'In Mobile browsers, the sidebar is viewable as a Slide Panel and should contain navigational content only.',
      'En navegadores móviles, la barra lateral se ve como un Slide Panel y solo debe contener contenido de navegación.'
    ),
    articleHeaderTitle: pickString(
      locale,
      'article_header_title',
      'Grid with Sidebar Article',
      'Artículo de cuadrícula con barra lateral'
    ),
    headingButton: pickString(locale, 'heading_button', 'Heading Button', 'Botón de encabezado'),
    heading1: pickString(locale, 'heading_level_1', 'Heading 1', 'Encabezado 1'),
    heading2: pickString(locale, 'heading_level_2', 'Heading 2', 'Encabezado 2'),
    heading3: pickString(locale, 'heading_level_3', 'Heading 3', 'Encabezado 3'),
    heading4: pickString(locale, 'heading_level_4', 'Heading 4', 'Encabezado 4'),
    heading5: pickString(locale, 'heading_level_5', 'Heading 5', 'Encabezado 5'),
    heading6: pickString(locale, 'heading_level_6', 'Heading 6', 'Encabezado 6'),
    articleCardBody: pickString(
      locale,
      'article_card_body',
      'Article using the card styles for visual consistency.',
      'Artículo usando estilos de tarjeta para mantener la consistencia visual.'
    ),
  };
}
